import { useState, useEffect } from "react";
import { useNavigate }          from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient, getUser }   from "../../api/apiServices";

export default function InternalTransferPage() {
  const navigate = useNavigate();

  const [step, setStep]   = useState("lookup");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount]       = useState("");
  const [note, setNote]           = useState("");
  const [pin, setPin]             = useState("");
  const [feeCfg, setFeeCfg]       = useState({ feePercent: 0, feeFlat: 0 });
  const [balance, setBalance]     = useState(null);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    apiClient.get("/mtrade/transfer/config")
      .then(r => { if (r.data.status) setFeeCfg(r.data.data); })
      .catch(() => {});
    getUser()
      .then(d => { if (d?.status) setBalance(d.user?.wallet?.nairaBalance ?? null); })
      .catch(() => {});
  }, []);

  const rawAmount = Number(amount) || 0;
  const fee       = Math.round((rawAmount * (feeCfg.feePercent || 0)) / 100) + (feeCfg.feeFlat || 0);
  const total     = rawAmount + fee;

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3 || /^\d+$/.test(q)) { setSuggestions([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await apiClient.get(`/mtrade/transfer/search?q=${encodeURIComponent(q)}`);
        if (r.data.status) setSuggestions(r.data.data);
      } catch {}
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const pickRecipient = (u) => {
    setRecipient(u);
    setSuggestions([]);
    setStep("amount");
  };

  const handleLookup = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const r = await apiClient.post("/mtrade/transfer/lookup", { query: query.trim() });
      if (r.data.status) {
        setRecipient(r.data.data);
        setStep("amount");
      } else {
        toast.error(r.data.message || "User not found");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "User not found");
    } finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (pin.length !== 4) return toast.error("Enter your 4-digit PIN");
    setLoading(true);
    try {
      const r = await apiClient.post("/mtrade/transfer/send", {
        recipientUserId: recipient.userId,
        amount:          rawAmount,
        note:            note.trim(),
        transactionPin:  pin,
      });
      if (r.data.status) {
        toast.success(r.data.message);
        navigate("/transactions");
      } else {
        toast.error(r.data.message || "Transfer failed");
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      const msg  = err?.response?.data?.message || "Transfer failed";
      toast.error(msg);
      if (code === "PIN_INVALID" || code === "PIN_LOCKED") setPin("");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-bg">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button
            onClick={() => step === "lookup" ? navigate(-1) : setStep(step === "pin" ? "amount" : "lookup")}
            className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <div>
            <h1 className="text-primary font-bold text-lg font-display">Send Money</h1>
            <p className="text-muted text-xs">
              {step === "lookup" && "Find who you want to pay"}
              {step === "amount" && "Enter amount to send"}
              {step === "pin"    && "Confirm with your PIN"}
            </p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-5 pb-10 max-w-lg mx-auto space-y-5">

        {step === "lookup" && (
          <>
            <div>
              <label className="text-secondary text-sm font-semibold mb-2 block">
                Username, phone number, or account ID
              </label>
              <div className="bg-surface border-2 border-border-strong rounded-2xl flex items-center px-4 focus-within:border-brand transition-colors">
                <i className="ri-user-search-line text-muted text-base mr-2" />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLookup()}
                  placeholder="e.g. johndoe or 080..."
                  className="flex-1 py-4 outline-none text-primary text-sm bg-transparent placeholder:text-faint"
                />
              </div>
              <p className="text-faint text-[11px] mt-1.5 px-1">
                Start typing a username to see suggestions, or enter a full phone number / account ID.
              </p>
            </div>

            {suggestions.length > 0 && (
              <div className="bg-surface border border-border rounded-2xl shadow-card overflow-hidden divide-y divide-border -mt-1">
                {suggestions.map(u => (
                  <button key={u.userId} onClick={() => pickRecipient(u)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-hover active:bg-surface-hover text-left transition-colors">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: "var(--color-brand-light)", boxShadow: "inset 0 0 0 1px var(--color-brand-ring)" }}>
                      {u.avatar
                        ? <img src={u.avatar} className="w-full h-full object-cover" alt="" />
                        : <span className="font-bold text-sm" style={{ color: "var(--color-brand-text)" }}>{u.name?.charAt(0) || "?"}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-primary text-sm font-semibold truncate">{u.name}</p>
                      <p className="text-muted text-xs">@{u.userName}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-faint text-lg" />
                  </button>
                ))}
              </div>
            )}

            <button onClick={handleLookup} disabled={!query.trim() || loading}
              className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
              {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><i className="ri-search-line" />Find Recipient</>}
            </button>
          </>
        )}

        {step === "amount" && recipient && (
          <>
            <div className="bg-surface border border-border shadow-card rounded-2xl px-4 py-3.5 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: "var(--color-brand-light)", boxShadow: "inset 0 0 0 1px var(--color-brand-ring)" }}>
                {recipient.avatar
                  ? <img src={recipient.avatar} className="w-full h-full object-cover" alt="" />
                  : <span className="font-bold" style={{ color: "var(--color-brand-text)" }}>{recipient.name?.charAt(0) || "?"}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-primary font-semibold text-sm truncate">{recipient.name}</p>
                <p className="text-muted text-xs">@{recipient.userName}{recipient.phoneHint ? ` · ${recipient.phoneHint}` : ""}</p>
              </div>
              <i className="ri-checkbox-circle-fill text-lg" style={{ color: "var(--color-brand)" }} />
            </div>

            <div>
              <label className="text-secondary text-sm font-semibold mb-2 block">Amount</label>
              <div className="bg-surface border-2 border-border-strong rounded-2xl flex items-center px-4 focus-within:border-brand transition-colors">
                <span className="text-muted font-bold text-lg mr-2">₦</span>
                <input
                  type="number" value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 py-4 outline-none text-primary text-xl font-bold bg-transparent placeholder:text-faint"
                />
              </div>
              {balance !== null && (
                <p className="text-faint text-xs mt-1.5 px-1">Balance: ₦{Number(balance).toLocaleString()}</p>
              )}
            </div>

            <div>
              <label className="text-secondary text-sm font-semibold mb-2 block">Note (optional)</label>
              <input
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="What's this for?"
                maxLength={100}
                className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3.5 text-sm text-primary outline-none focus:border-brand placeholder:text-faint transition-colors"
              />
            </div>

            {rawAmount > 0 && (
              <div className="bg-surface-muted border border-border-strong rounded-2xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted">Amount</span><span className="font-semibold text-primary">₦{rawAmount.toLocaleString()}</span></div>
                {fee > 0 && <div className="flex justify-between"><span className="text-muted">Fee</span><span className="font-semibold" style={{ color: "var(--color-danger)" }}>+ ₦{fee.toLocaleString()}</span></div>}
                <div className="h-px bg-border" />
                <div className="flex justify-between"><span className="text-secondary font-bold">Total</span><span className="font-bold text-primary">₦{total.toLocaleString()}</span></div>
              </div>
            )}

            <button onClick={() => setStep("pin")} disabled={rawAmount <= 0}
              className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95">
              Continue
            </button>
          </>
        )}

        {step === "pin" && recipient && (
          <>
            <div className="bg-surface border border-border shadow-card rounded-2xl px-4 py-4 text-center">
              <p className="text-faint text-xs mb-1">Sending to {recipient.name}</p>
              <p className="text-primary font-bold text-3xl font-display">₦{rawAmount.toLocaleString()}</p>
              {fee > 0 && <p className="text-faint text-xs mt-1">+ ₦{fee.toLocaleString()} fee · ₦{total.toLocaleString()} total</p>}
            </div>

            <div>
              <label className="text-secondary text-sm font-semibold mb-3 block text-center">Enter your transaction PIN</label>
              <div className="flex justify-center">
                <input
                  type="password" inputMode="numeric" maxLength={4}
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                  className="w-40 text-center text-3xl tracking-[0.5em] font-bold bg-surface border-2 border-border-strong rounded-2xl py-4 outline-none focus:border-brand placeholder:text-faint"
                  autoFocus
                />
              </div>
            </div>

            <button onClick={handleSend} disabled={pin.length !== 4 || loading}
              className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
              {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><i className="ri-send-plane-fill" />Send ₦{rawAmount.toLocaleString()}</>}
            </button>
            <p className="text-center text-faint text-xs">🔒 Transfers are instant and cannot be reversed</p>
          </>
        )}
      </div>
    </div>
  );
}
